'use client';

import { useEffect, useState } from 'react';
import Image from 'next/image';
import clsx from 'clsx';
import { Icon, Typography } from '@/components';
import { NavItem } from './NavItem';
// import { MobileStickers } from './MobileStickers';
import useScrolled from '@/hooks/useScrolled';
import { Cross, MainLogo, Linkedin, Hamburger, X, RubyPlay } from '@/assets';
import { NAV_ITEMS } from '@/constants/NavItems';

const SOCIAL_LINKS = [
  {
    component: Linkedin,
    href: 'https://linkedin.com/company/splash-technology-uk',
    name: 'linkedin',
  },
  {
    component: X,
    href: 'https://twitter.com/splashtech',
    name: 'twitter',
  },
];

const Navbar = () => {
  const [isMobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isLinkClicked, setIsLinkClicked] = useState(false);
  const { isScrollingDown, isScrolled } = useScrolled();

  const toggleMobileMenu = () => {
    if (isMobileMenuOpen) {
      setIsLinkClicked(false);
    }
    setMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleClickLink = () => {
    setIsLinkClicked(true);
    setMobileMenuOpen(false);
  };

  useEffect(() => {
    // Prevent scrolling when navbar menu is expanded
    if (isMobileMenuOpen) {
      document.body.style.opacity = 'hidden';
      document.body.style.overflow = 'hidden';
    }

    const mediaQuery = window.matchMedia('(min-width: 769px)');
    const handleMediaChange = (event: MediaQueryListEvent) => {
      if (event.matches) {
        setMobileMenuOpen(false);
      }
    };
    mediaQuery.addEventListener('change', handleMediaChange);

    return () => {
      document.body.style.overflow = 'unset';
      mediaQuery.removeEventListener('change', handleMediaChange);
    };
  }, [isMobileMenuOpen, isLinkClicked]);

  return (
    <nav
      className={clsx(
        'text-neutral-0 fixed left-0 right-0 z-[100] flex-col justify-between transition-all duration-200 ease-in-out hidden md:flex overflow-hidden bg-transparent',
        {
          'top-0': isScrolled,
          'top-11': !isScrolled,
          'bg-neutral-900': !isScrolled,
          'bg-neutral-900-transparent backdrop-blur-[19px]': true,
          'h-[104px]': !isScrolled && !isMobileMenuOpen,
          'h-[96px]': isScrolled && !isMobileMenuOpen,
          'h-screen': isMobileMenuOpen && isScrolled,
          'h-[calc(100vh-44px)]': isMobileMenuOpen && !isScrolled,
          '-translate-y-full': isScrollingDown,
          'bg-transparent': !isMobileMenuOpen,
          'bg-black': isMobileMenuOpen,
        }
      )}>
      <div>
        {/* <MobileStickers /> */}

        <div
          className={clsx('flex justify-between items-start px-6 transition-all duration-200 ease-in-out', {
            'pb-4 pt-14': !isScrolled,
            'py-8': isScrolled,
          })}>
          <div className='w-8' />
          <div className='flex justify-center  h-8'>
            <Image src={MainLogo} width={107} height={32} alt='Splash Tech' />
          </div>
          <button type='submit' onClick={toggleMobileMenu} aria-label='Navbar menu'>
            <Icon
              Component={isMobileMenuOpen ? Cross : Hamburger}
              className='relative md:-top-[5px] fill-neutral-0 cursor-pointer transition-all duration-200 ease-in-out hover:fill-secondary'
            />
          </button>
        </div>

        <ul className='pt-12 flex flex-col gap-8 items-center'>
          {[...NAV_ITEMS.LEFT, ...NAV_ITEMS.RIGHT].map(({ href, label }) => (
            <NavItem onClick={handleClickLink} key={label} href={href} label={label} />
          ))}
        </ul>
      </div>

      <div>
        <div className='flex items-center justify-center gap-2 px-4 py-8'>
          <Typography tag='p' size='bodyExtraSmall' className='text-neutral-0'>
            Part of
          </Typography>
          <Image src={RubyPlay} height={24} alt='RubyPlay' />
        </div>

        <div className='border-solid border-t-[1px] h-[154px] px-6 flex flex-col justify-center items-center gap-8'>
          <div className='flex justify-center gap-8'>
            {SOCIAL_LINKS.map(({ href, component, name }) => (
              <a key={href} href={href} target='_blank' aria-label={name} rel='noreferrer'>
                <Icon
                  Component={component}
                  className='fill-neutral-0 cursor-pointer transition-all duration-200 ease-in-out hover:fill-secondary'
                />
              </a>
            ))}
          </div>
          <Typography tag='p' size='bodySmall' className='text-sm' weight='normal'>
            © 2026 all rights reserved
          </Typography>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
